import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"
import _imports_0 from '@/assets.app/images/logo.svg'
import _imports_1 from '@/assets.app/images/onboarding/email.svg'


const _hoisted_1 = { class: "onboarding-signup" }
const _hoisted_2 = {
  key: 0,
  class: "onboarding-signup__loading-screen"
}
const _hoisted_3 = { class: "onboarding-signup__loading-screen-inner" }
const _hoisted_4 = { class: "onboarding-signup__banner" }
const _hoisted_5 = { class: "onboarding-signup__form--tablet" }
const _hoisted_6 = { class: "onboarding-signup__buttons" }
const _hoisted_7 = { class: "onboarding-signup__links" }
const _hoisted_8 = { class: "onboarding-signup__login" }
const _hoisted_9 = {
  key: 1,
  class: "sign-up onboarding-signup__form--desktop"
}
const _hoisted_10 = { class: "sign-up__form-box" }
const _hoisted_11 = { class: "shortform-sign-up" }
const _hoisted_12 = { class: "shortform-sign-up__group" }
const _hoisted_13 = {
  key: 0,
  class: "shortform-sign-up__group"
}
const _hoisted_14 = {
  key: 1,
  class: "text-muted font-italic"
}
const _hoisted_15 = {
  key: 0,
  class: "text-success font-weight-700"
}
const _hoisted_16 = {
  key: 1,
  class: "text-info font-weight-700"
}
const _hoisted_17 = {
  key: 2,
  class: "text-warning font-weight-700"
}
const _hoisted_18 = {
  key: 3,
  class: "text-danger font-weight-700"
}
const _hoisted_19 = {
  key: 4,
  class: "text-danger font-weight-700"
}
const _hoisted_20 = {
  key: 2,
  class: "alert alert-danger"
}
const _hoisted_21 = { class: "shortform-sign-up__group shortform-sign-up__group--terms" }
const _hoisted_22 = { class: "shortform-sign-up__terms" }
const _hoisted_23 = { class: "shortform-sign-up__group shortform-sign-up__group--submit" }
const _hoisted_24 = { class: "shortform-sign-up__submit button button--theme" }
const _hoisted_25 = { class: "shortform-sign-up__log-in" }

import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
} from 'vue'
import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router'

import { useSignupAuth } from '@/models/auth/useSignupAuth'
import { GoogleAuthView } from '@/models/auth/google'
import { auth } from '@/services/auth'
import { authCssFix } from '@/helpers/html'
import { waitTimer } from '@/helpers/timer'
import { TABLET_WIDTH } from '@/init/settings'
import { passwordlessSignup18304Variant } from '@/services/ab/experiments'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import SocialAuth from '@/app/components/auth/SocialAuth.vue'
import AuthPartition from '@/app/components/auth/AuthPartition.vue'
import AppleSignInButton from '@/app/components/auth/AppleSignInButton.vue'
import FacebookLoginButton from '@/app/components/auth/FacebookLoginButton.vue'
import GoogleSignInButton from '@/app/components/auth/GoogleSignInButton.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import SignupSheet from '../signup/SignupSheet.vue'
import Slider from '../signup/Slider.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Signup',
  props: {
    nextOnboardingPage: { type: Function }
  },
  setup(__props: any) {

const props = __props

const route = useRoute()
const router = useRouter()
const {
  signupView: view,
  socialView,
  authMode,
  initSocialAuth,
  prefillEmail,
  startEmailSignup,
} = useSignupAuth({
  onSuccess: async (nextPage: RouteLocationRaw | null): Promise<void> => {
    const { timeout, routeTarget } = getPostAuthRedirect(nextPage ?? undefined)

    await waitTimer(timeout)

    if (routeTarget) {
      await router.push(routeTarget)
    } else {
      props.nextOnboardingPage()
    }
  },
})

const bottomSheetRef = useTemplateRef('bottomSheetRef')

const showGoogleSignupOnMobile = ref(false)

const passwordlessSignup = computed(
  () => passwordlessSignup18304Variant() === 'B',
)

function updateShowGoogleSignupOnMobile(): void {
  showGoogleSignupOnMobile.value = window.innerWidth <= TABLET_WIDTH

  if (
    socialView.value &&
    socialView.value.googleAuthView instanceof GoogleAuthView
  ) {
    const googleAuthView = socialView.value.googleAuthView as GoogleAuthView
    setTimeout(() => googleAuthView.renderButtons(), 100)
  }
}

function openInfoSheet(): void {
  bottomSheetRef.value?.show()
}

/**
 * Resolve the post-auth redirect target and delay for onboarding signup.
 */
function getPostAuthRedirect(nextPage?: RouteLocationRaw): {
  timeout: number
  routeTarget: RouteLocationRaw | null
} {
  // Timeout before the redirect to let Mixpanel calls complete.
  // It is less problematic in the main app, where Mixpanel would track
  // missing events on the next page, but in the extension, the next page
  // is the extension auth page, which closes immediately and Mixpanel
  // calls are lost, so we set a longer timeout in this case.
  // See #9509.
  if (router.currentRoute.value.query?.redirect === '/extension-auth') {
    return {
      routeTarget: { name: 'extension_auth' },
      timeout: 3000,
    }
  }

  return {
    routeTarget: nextPage ?? null,
    timeout: 100,
  }
}

onBeforeMount(async () => {
  await initSocialAuth()

  updateShowGoogleSignupOnMobile()
  window.addEventListener('resize', updateShowGoogleSignupOnMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateShowGoogleSignupOnMobile)
})

onMounted(async () => {
  // If user name was passed a prop (from the signup with email form)
  if (route.query.email) {
    prefillEmail(route.query.email as string)
  }

  authCssFix()
})

const loginRoute = computed(() => {
  const routeData = { name: 'login', query: route.query }
  // If we have the email set, pass it on to the signup page.
  if (view.value.model.email) {
    routeData.query.email = view.value.model.email
  }
  return routeData
})

const isTemporaryUser = computed(() => {
  return auth.isTemporaryUser()
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_v_wait = _resolveComponent("v-wait")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.$wait.is('Register'))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "onboarding-signup__loading-text" }, "Creating account", -1)),
            _createVNode(ButtonSpinner)
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (isTemporaryUser.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "onboarding-signup__back-15807",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).back()))
          }, [...(_cache[5] || (_cache[5] = [
            _createElementVNode("span", { class: "iconfont iback" }, null, -1),
            _createTextVNode(" Back ", -1)
          ]))]))
        : _createCommentVNode("", true),
      _cache[6] || (_cache[6] = _createElementVNode("div", null, [
        _createElementVNode("div", { class: "onboarding-signup__logo-wrapper" }, [
          _createElementVNode("img", {
            class: "onboarding-signup__logo",
            src: _imports_0,
            alt: "Shortform App"
          })
        ])
      ], -1)),
      _createVNode(Slider)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(BasePopup, {
        ref_key: "bottomSheetRef",
        ref: bottomSheetRef,
        class: "onboarding-signup__sheet"
      }, {
        default: _withCtx(() => [
          _createVNode(SignupSheet, {
            view: _unref(view),
            showNameInput: !passwordlessSignup.value,
            passwordless: passwordlessSignup.value
          }, null, 8, ["view", "showNameInput", "passwordless"])
        ]),
        _: 1
      }, 512),
      _createElementVNode("div", _hoisted_6, [
        (_unref(socialView)?.googleAuthView && showGoogleSignupOnMobile.value)
          ? (_openBlock(), _createBlock(GoogleSignInButton, {
              key: 0,
              authMode: _unref(authMode),
              buttonLayout: "mobile-stacked",
              view: _unref(socialView).googleAuthView,
              showButton: "big"
            }, null, 8, ["authMode", "view"]))
          : _createCommentVNode("", true),
        (_unref(socialView)?.appleAuthView)
          ? (_openBlock(), _createBlock(AppleSignInButton, {
              key: 1,
              class: "onboarding-signup__button--apple",
              view: _unref(socialView).appleAuthView,
              title: "Sign up with"
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (_unref(socialView)?.facebookAuthView)
          ? (_openBlock(), _createBlock(FacebookLoginButton, {
              key: 2,
              title: "Sign up with",
              view: _unref(socialView).facebookAuthView,
              class: "onboarding-signup__button--facebook"
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "button shortform-sign-up__button onboarding-signup__button--email",
          onClick: openInfoSheet
        }, [...(_cache[7] || (_cache[7] = [
          _createElementVNode("img", {
            class: "onboarding-signup__button-image",
            src: _imports_1,
            alt: ""
          }, null, -1),
          _createElementVNode("span", { class: "onboarding-signup__button-text" }, "Sign up with Email", -1)
        ]))])
      ]),
      _createElementVNode("p", _hoisted_7, [
        _cache[10] || (_cache[10] = _createTextVNode(" By creating an account, you agree to the ", -1)),
        _createVNode(_component_router_link, { to: { name: 'site_terms' } }, {
          default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
            _createTextVNode("Terms of Service", -1)
          ]))]),
          _: 1
        }),
        _cache[11] || (_cache[11] = _createTextVNode(" and ", -1)),
        _createVNode(_component_router_link, { to: { name: 'site_privacy' } }, {
          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
            _createTextVNode("Privacy Policy", -1)
          ]))]),
          _: 1
        }),
        _cache[12] || (_cache[12] = _createTextVNode(". ", -1))
      ]),
      _createElementVNode("div", _hoisted_8, [
        _cache[14] || (_cache[14] = _createTextVNode(" Already have an account? ", -1)),
        _createVNode(_component_router_link, {
          to: { name: 'login', query: _unref(route).query }
        }, {
          default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
            _createTextVNode("Log in", -1)
          ]))]),
          _: 1
        }, 8, ["to"])
      ])
    ]),
    (_unref(view))
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _cache[27] || (_cache[27] = _createElementVNode("h2", { class: "shortform-sign-up__title" }, "Sign Up to Shortform", -1)),
              _createElementVNode("form", {
                class: "shortform-sign-up__form",
                role: "form",
                novalidate: "",
                onSubmit: _cache[3] || (_cache[3] = _withModifiers(
//@ts-ignore
(...args) => (_unref(startEmailSignup) && _unref(startEmailSignup)(...args)), ["prevent"]))
              }, [
                _createElementVNode("div", _hoisted_12, [
                  _cache[15] || (_cache[15] = _createElementVNode("label", {
                    class: "shortform-sign-up__label",
                    for: "register_email"
                  }, "Email Address", -1)),
                  _createVNode(InputWithContextMenu, {
                    id: "register_email",
                    modelValue: _unref(view).model.email,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(view).model.email) = $event)),
                    class: _normalizeClass(["shortform-sign-up__input", _unref(view).validation.stateClass('email')]),
                    type: "email",
                    placeholder: "",
                    autofocus: ""
                  }, null, 8, ["modelValue", "class"]),
                  _createVNode(FormError, {
                    errors: _unref(view).validation.errors,
                    field: "email"
                  }, null, 8, ["errors"])
                ]),
                (!passwordlessSignup.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _cache[16] || (_cache[16] = _createElementVNode("label", {
                        class: "shortform-sign-up__label",
                        for: "register_password"
                      }, "Password", -1)),
                      _createVNode(InputWithContextMenu, {
                        id: "register_password",
                        modelValue: _unref(view).model.password,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(view).model.password) = $event)),
                        type: "password",
                        autocomplete: "new-password",
                        placeholder: "",
                        class: _normalizeClass(["shortform-sign-up__input", _unref(view).validation.stateClass('password')])
                      }, null, 8, ["modelValue", "class"]),
                      _createVNode(FormError, {
                        errors: _unref(view).validation.errors,
                        field: "password"
                      }, null, 8, ["errors"])
                    ]))
                  : _createCommentVNode("", true),
                (!passwordlessSignup.value && _unref(view).model.password_score)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createElementVNode("small", null, [
                        _cache[17] || (_cache[17] = _createTextVNode(" password strength: ", -1)),
                        (_unref(view).model.password_score === '4')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_15, "strong"))
                          : _createCommentVNode("", true),
                        (_unref(view).model.password_score === '3')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_16, "good"))
                          : _createCommentVNode("", true),
                        (_unref(view).model.password_score === '2')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_17, "medium"))
                          : _createCommentVNode("", true),
                        (_unref(view).model.password_score === '1')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_18, "weak"))
                          : _createCommentVNode("", true),
                        (_unref(view).model.password_score === '0')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_19, "terrible"))
                          : _createCommentVNode("", true)
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (_unref(view).validation.errors && _unref(view).validation.errors._schema)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, " {view.validation.errors._schema} "))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("span", _hoisted_22, [
                    _cache[20] || (_cache[20] = _createTextVNode("By creating an account, you agree to the ", -1)),
                    _createVNode(_component_router_link, {
                      to: { name: 'site_terms' },
                      class: "black",
                      target: "_blank"
                    }, {
                      default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                        _createTextVNode("Terms of Service", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[21] || (_cache[21] = _createTextVNode(" and ", -1)),
                    _createVNode(_component_router_link, {
                      to: { name: 'site_privacy' },
                      class: "black",
                      target: "_blank"
                    }, {
                      default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                        _createTextVNode("Privacy Policy", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[22] || (_cache[22] = _createTextVNode(". ", -1))
                  ])
                ]),
                _createElementVNode("div", _hoisted_23, [
                  _createVNode(_component_v_wait, {
                    for: "Register",
                    style: {"display":"flex"}
                  }, {
                    waiting: _withCtx(() => [
                      _createElementVNode("div", _hoisted_24, [
                        _cache[23] || (_cache[23] = _createTextVNode(" Creating Account ", -1)),
                        _createVNode(ButtonSpinner)
                      ])
                    ]),
                    default: _withCtx(() => [
                      _cache[24] || (_cache[24] = _createElementVNode("button", {
                        class: "shortform-sign-up__submit button button--theme",
                        type: "submit"
                      }, " Create Account ", -1))
                    ]),
                    _: 1
                  })
                ])
              ], 32),
              _createVNode(AuthPartition, { mobileText: "Sign up with" }),
              (_unref(socialView))
                ? (_openBlock(), _createBlock(SocialAuth, {
                    key: 0,
                    class: "shortform-sign-up__social-auth",
                    view: _unref(socialView),
                    authMode: _unref(authMode),
                    googleButtonLayout: "signup-desktop",
                    showGoogle: !showGoogleSignupOnMobile.value
                  }, null, 8, ["view", "authMode", "showGoogle"]))
                : _createCommentVNode("", true),
              _createElementVNode("p", _hoisted_25, [
                _cache[26] || (_cache[26] = _createTextVNode(" Already have an account? ", -1)),
                _createVNode(_component_router_link, {
                  to: loginRoute.value,
                  style: {"color":"#ffb600"}
                }, {
                  default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                    _createElementVNode("strong", null, "Log in", -1)
                  ]))]),
                  _: 1
                }, 8, ["to"])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})